import {FactoryFn} from 'src/app/data/model/factory-function';
import {
  Product as AppProduct,
  ProductComponentModel,
  ProductReference,
  Specification,
  SpecificationOption,
  Variant,
} from '.';
import {ContextData} from '../../common/context-data';
import {Gear} from './gear';
import {
  Image,
  isDisplayableImageSrc,
  resolveFirstDisplayableImage,
} from './image';
import {
  Bundle,
  DisplayMetadata,
  Product as ClareProduct,
  Variation,
} from '@sportngin/se-commerce-sdk/models/clare';
import {pluckClarePrice} from './clare-helpers';
import {clareVariationToVariant} from './variant';

function isClareBundle(source: ClareProduct | Bundle): source is Bundle {
  return 'products' in source && Array.isArray(source.products);
}

/** Display rows from Clare display_metadata for YSO product cards. */
function specsFromItemDisplayMetadata(
  displayMetadata?: DisplayMetadata | null,
): Array<{name: string; value: string}> {
  const specs: Array<{name: string; value: string}> = [];
  if (!displayMetadata) {
    return specs;
  }
  if (displayMetadata.brand) {
    specs.push({name: 'Brand', value: displayMetadata.brand});
  }
  if (displayMetadata.gender) {
    specs.push({name: 'Gender', value: displayMetadata.gender});
  }
  if (displayMetadata.fulfilled_by) {
    specs.push({
      name: 'Fulfilled By',
      value: displayMetadata.fulfilled_by,
    });
  }
  if (displayMetadata.disclaimer) {
    specs.push({
      name: 'Disclaimer',
      value: displayMetadata.disclaimer,
    });
  }
  return specs;
}

function collectItemImages(source: ClareProduct | Bundle): Image[] {
  const images = (source.display_metadata?.images ?? []).filter(img =>
    isDisplayableImageSrc(img?.src),
  );
  if (
    images.length === 0 &&
    'image_url' in source &&
    isDisplayableImageSrc(source.image_url)
  ) {
    images.push({src: source.image_url!, alt: source.name ?? ''});
  }
  return images;
}

function resolveBundleIdFromItem(
  source: ClareProduct | Bundle,
): number | undefined {
  const fromMeta = source.display_metadata?.bundle_id;
  if (fromMeta !== undefined && Number.isFinite(Number(fromMeta))) {
    return Number(fromMeta);
  }
  if (!isClareBundle(source)) {
    return undefined;
  }
  const fromId = Number(source.id);
  return Number.isFinite(fromId) ? fromId : undefined;
}

export const ysoProductComponentKey = 'yso-products';

/**
 * Maps a Clare Item to a YsoProduct, including specs and variants for YSO catalogs.
 * @param source Clare Item
 * @param references Optional product references
 */
export const itemToYsoProduct: FactoryFn<ClareProduct | Bundle, YsoProduct> = (
  source: ClareProduct | Bundle,
  references?: ProductReference,
): YsoProduct => {
  const isBundle = isClareBundle(source);
  const specs = specsFromItemDisplayMetadata(source.display_metadata);
  const productSource = isBundle ? undefined : source;

  const variants =
    productSource?.variations?.map((v: Variation) =>
      clareVariationToVariant(v),
    ) ||
    (productSource?.default_variation
      ? [clareVariationToVariant(productSource.default_variation)]
      : []);

  let specifications: Specification[] = [];

  // Helper function to normalize spec names
  const normalizeName = (name: string): string => {
    if (!name) return name;
    const lowerName = name.toLowerCase().trim();

    if (lowerName === 'colors' || lowerName === 'color') return 'Color';
    if (lowerName === 'sizes' || lowerName === 'size') return 'Size';

    // Handle irregular plurals
    if (lowerName.endsWith('ies')) {
      return lowerName.charAt(0).toUpperCase() + lowerName.slice(1, -3) + 'y';
    }

    // Remove trailing 's' if present
    const singularName = lowerName.endsWith('s')
      ? lowerName.slice(0, -1)
      : lowerName;

    return singularName.charAt(0).toUpperCase() + singularName.slice(1);
  };

  // If specifications are missing, but variants are present, derive specifications from variants
  if ((!specifications || specifications.length === 0) && variants.length > 0) {
    const specsMap = new Map<string, Specification>();

    // Also normalize variant spec names by creating new variants with normalized specs
    const normalizedVariants = variants.map((variant: Variant) => {
      if (!variant.specs || variant.specs.length === 0) return variant;

      return {
        ...variant,
        specs: variant.specs.map(spec => ({
          ...spec,
          name: normalizeName(spec.name),
        })),
      };
    });

    // Replace original variants with normalized ones
    variants.length = 0;
    variants.push(...normalizedVariants);

    variants.forEach((variant: Variant) => {
      variant.specs?.forEach(spec => {
        const specName = normalizeName(spec.name);
        if (!specsMap.has(String(spec.specId))) {
          specsMap.set(
            String(spec.specId),
            new Specification({
              id: String(spec.specId),
              name: specName,
              ownerId: '',
              listOrder: spec.displayOrder,
              required: true,
              options: [],
            }),
          );
        }
        const specification = specsMap.get(String(spec.specId));
        if (
          specification &&
          !specification.options.some(
            opt => String(opt.id) === String(spec.optionId),
          )
        ) {
          specification.options.push(
            new SpecificationOption({
              id: String(spec.optionId),
              value: spec.value || spec.name,
              listOrder: 0,
            }),
          );
        }
      });
    });
    specifications = Array.from(specsMap.values());
  }

  const images = collectItemImages(source);

  const result = new YsoProduct({
    categoryId: null,
    description: source.description ?? '',
    id: source.id ? String(source.id) : '',
    catalogProductId: source.display_metadata?.product_id
      ? String(source.display_metadata.product_id)
      : source.id
        ? String(source.id)
        : undefined,
    name: source.name ?? '',
    price: pluckClarePrice(source),
    category: source.display_metadata?.category ?? '',
    gender: source.display_metadata?.gender ?? '',
    brand: source.display_metadata?.brand ?? '',
    age: source.display_metadata?.age
      ? String(source.display_metadata.age)
      : '',
    images,
    fulfillment: source.display_metadata?.fulfilled_by ?? '',
    references: references,
    maxQuantity: null,
    templateKey:
      source.display_metadata?.template_key ?? ysoProductComponentKey,
    specs,
    variants,
    specifications,
    type: isBundle ? 'Bundle' : 'Product',
    products: isBundle
      ? source.products.map((p: ClareProduct) =>
          itemToYsoProduct(p, references),
        )
      : undefined,
    bundleId: resolveBundleIdFromItem(source),
  });

  return result;
};

export const itemToYsoProductComponentModel: FactoryFn<
  Array<ClareProduct | Bundle>,
  ProductComponentModel<YsoProduct[]>
> = (
  src: Array<ClareProduct | Bundle>,
  context: ContextData,
  ref: ProductReference,
  catalogKey?: string,
) => {
  return new ProductComponentModel<YsoProduct[]>(
    ysoProductComponentKey,
    src.map(item => itemToYsoProduct(item, ref)),
    'Gear',
    `Get ready for ${context.signups[0]?.requestDetails.sport} with items from our ${context?.bossOrganization.name} store.`,
    false,
    catalogKey ??
      (src[0]?.catalog_id !== undefined
        ? String(src[0].catalog_id)
        : undefined),
  );
};

export interface YsoProductInterface extends AppProduct, Gear {
  /** Array of product specs from Clare */
  specs?: Array<{name: string; value: string}>;
  /** Array of product variants from Clare */
  variants?: Variant[];
  /** Full specification objects */
  specifications?: Specification[];
  /** Type of the item (e.g. Bundle, Product) */
  type?: string;
  /** Products within a bundle */
  products?: YsoProduct[];
  /** Clare bundle id for `bundle_id` on bundle cart adds. */
  bundleId?: number;
}

/**
 * YsoProduct model extended to support specs and variants for YSO catalogs.
 */
export class YsoProduct implements YsoProductInterface {
  categoryId: string | null;
  catalogProductId?: string;
  description: string;
  id: string;
  images: Image[];
  name: string;
  price: number;
  gender: string;
  category: string;
  brand: string;
  age: string;
  fulfillment: string;
  references: ProductReference;
  maxQuantity: number | null;
  templateKey: string;
  specs?: Array<{name: string; value: string}>;
  variants?: Variant[];
  specifications?: Specification[];
  type?: string;
  products?: YsoProduct[];
  bundleId?: number;

  constructor(source?: YsoProductInterface) {
    if (!source) return;

    this.categoryId = source.categoryId;
    this.catalogProductId = source.catalogProductId;
    this.description = source.description;
    this.id = source.id;
    this.name = source.name;
    this.price = source.price;
    this.category = source.category;
    this.gender = source.gender;
    this.brand = source.brand;
    this.age = source.age;
    this.images = [...source.images];
    this.fulfillment = source.fulfillment ?? '';
    this.references = source.references ?? {};
    this.maxQuantity = source.maxQuantity ?? null;
    this.templateKey = source.templateKey;
    this.specs = source.specs ?? [];
    this.variants = source.variants ?? [];
    this.specifications = source.specifications ?? [];
    this.type = source.type;
    this.products = source.products ?? [];
    this.bundleId = source.bundleId;
  }

  get image(): Image {
    return resolveFirstDisplayableImage(this.images);
  }
}
